import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('❌ API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// API Service Functions
export const liquorAPI = {
  // Get all liquor types
  getLiquorTypes: async () => {
    try {
      const response = await apiClient.get('/liquor-types');
      return response.data;
    } catch (error) {
      console.warn('Using mock data for liquor types');
      // Fallback to mock data if API fails
      return [
        {
          id: 'gin',
          name: 'Gin',
          description: 'Classico gin botanico personalizzabile',
          basePrice: 45.00,
          image: 'https://images.unsplash.com/photo-1551538827-9c037cb4f32a?w=400&h=600&fit=crop'
        },
        {
          id: 'amaro',
          name: 'Amaro',
          description: 'Amaro tradizionale italiano',
          basePrice: 48.00,
          image: 'https://images.unsplash.com/photo-1569529465841-dfecdab7503b?w=400&h=600&fit=crop'
        },
        {
          id: 'bitter',
          name: 'Bitter',
          description: 'Bitter aromatico e intenso',
          basePrice: 42.00,
          image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=600&fit=crop'
        }
      ];
    }
  },

  // Get specific liquor type
  getLiquorType: async (liquorId) => {
    try {
      const response = await apiClient.get(`/liquor-types/${liquorId}`);
      return response.data;
    } catch (error) {
      console.warn(`Using mock data for liquor type: ${liquorId}`);
      return null;
    }
  }
};

export const ingredientsAPI = {
  // Get all ingredients
  getIngredients: async (category = null) => {
    try {
      const url = category ? `/ingredients/${category}` : '/ingredients/';
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.warn('Using mock data for ingredients');
      // Fallback to mock data
      const mockIngredients = {
        botanicals: [
          { id: 'juniper', name: 'Ginepro', category: 'botanicals', price: 2.00 },
          { id: 'coriander', name: 'Coriandolo', category: 'botanicals', price: 1.50 },
          { id: 'angelica', name: 'Angelica', category: 'botanicals', price: 2.50 },
          { id: 'orris', name: 'Iris', category: 'botanicals', price: 3.00 },
          { id: 'cardamom', name: 'Cardamomo', category: 'botanicals', price: 2.80 },
          { id: 'lavender', name: 'Lavanda', category: 'botanicals', price: 2.20 }
        ],
        citrus: [
          { id: 'lemon', name: 'Limone', category: 'citrus', price: 1.80 },
          { id: 'orange', name: 'Arancia', category: 'citrus', price: 1.60 },
          { id: 'bergamot', name: 'Bergamotto', category: 'citrus', price: 2.40 },
          { id: 'grapefruit', name: 'Pompelmo', category: 'citrus', price: 2.00 }
        ],
        spices: [
          { id: 'cinnamon', name: 'Cannella', category: 'spices', price: 1.90 },
          { id: 'ginger', name: 'Zenzero', category: 'spices', price: 2.10 },
          { id: 'nutmeg', name: 'Noce Moscata', category: 'spices', price: 2.60 },
          { id: 'clove', name: 'Chiodi di Garofano', category: 'spices', price: 2.30 }
        ]
      };
      
      if (category && mockIngredients[category]) {
        return mockIngredients[category];
      }
      
      return [...mockIngredients.botanicals, ...mockIngredients.citrus, ...mockIngredients.spices];
    }
  },

  // Liquor category configuration (admin only)
  getLiquorCategoryConfig: async (token) => {
    try {
      const response = await apiClient.get('/ingredients/admin/liquor-category-config', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateLiquorCategoryConfig: async (token, config) => {
    try {
      const response = await apiClient.put('/ingredients/admin/liquor-category-config', config, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Ingredient categories management (admin only)
  getIngredientCategories: async (token) => {
    try {
      const response = await apiClient.get('/ingredients/admin/categories', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createIngredientCategory: async (token, categoryData) => {
    try {
      const response = await apiClient.post('/ingredients/admin/categories', categoryData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateIngredientCategory: async (token, categoryId, categoryData) => {
    try {
      const response = await apiClient.put(`/ingredients/admin/categories/${categoryId}`, categoryData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteIngredientCategory: async (token, categoryId) => {
    try {
      const response = await apiClient.delete(`/ingredients/admin/categories/${categoryId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  toggleIngredientCategoryActive: async (token, categoryId) => {
    try {
      const response = await apiClient.put(`/ingredients/admin/categories/${categoryId}/toggle-active`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export const collectionsAPI = {
  // Get all collections (new dynamic endpoint)
  getCollections: async () => {
    try {
      const response = await apiClient.get('/collections/');
      return response.data.collections || [];
    } catch (error) {
      console.warn('Collections API error:', error);
      return [];
    }
  },

  // Get featured collections
  getFeaturedCollections: async (limit = 3) => {
    try {
      const response = await apiClient.get(`/collections/featured?limit=${limit}`);
      return response.data.collections || [];
    } catch (error) {
      console.warn('Featured collections API error:', error);
      return [];
    }
  },

  // Get collection by slug
  getCollectionBySlug: async (slug) => {
    try {
      const response = await apiClient.get(`/collections/${slug}`);
      return response.data.collection;
    } catch (error) {
      console.warn(`Collection API error for slug ${slug}:`, error);
      return null;
    }
  },

  // Get all premium collections (legacy - for backward compatibility)
  getPremiumCollections: async () => {
    try {
      const response = await apiClient.get('/premium-collections');
      return response.data;
    } catch (error) {
      console.warn('Using mock data for premium collections');
      // Fallback to mock data
      return [
        {
          id: 'around-the-world',
          name: 'Around The World ✈️🌍',
          description: 'Questa serie esclusiva di ricette celebra l\'atmosfera vibrante e i sapori unici delle più iconiche metropoli del mondo.',
          image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop',
          recipes: [
            { id: 'london', name: 'London Dry', ingredients: ['juniper', 'coriander', 'lemon'], price: 52.00 },
            { id: 'tokyo', name: 'Tokyo Nights', ingredients: ['juniper', 'ginger', 'bergamot'], price: 54.00 },
            { id: 'new-york', name: 'New York Classic', ingredients: ['juniper', 'orange', 'cinnamon'], price: 53.00 }
          ]
        },
        {
          id: 'stagioni-del-gusto',
          name: 'Stagioni del Gusto',
          description: 'Questa collezione di gin è ispirata alle stagioni dell\'anno, offrendo ricette che catturano i sapori e gli aromi distintivi.',
          image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=600&fit=crop',
          recipes: [
            { id: 'primavera', name: 'Primavera', ingredients: ['juniper', 'lavender', 'lemon'], price: 50.00 },
            { id: 'estate', name: 'Estate', ingredients: ['juniper', 'orange', 'cardamom'], price: 51.00 },
            { id: 'autunno', name: 'Autunno', ingredients: ['juniper', 'cinnamon', 'nutmeg'], price: 52.00 },
            { id: 'inverno', name: 'Inverno', ingredients: ['juniper', 'clove', 'ginger'], price: 53.00 }
          ]
        }
      ];
    }
  },

  // Get specific collection
  getCollection: async (collectionId) => {
    try {
      const response = await apiClient.get(`/premium-collections/${collectionId}`);
      return response.data;
    } catch (error) {
      console.warn(`Using mock data for collection: ${collectionId}`);
      return null;
    }
  }
};

export const reviewsAPI = {
  // Get reviews
  getReviews: async (limit = 20, featuredOnly = false) => {
    try {
      const response = await apiClient.get(`/reviews?limit=${limit}&featured_only=${featuredOnly}`);
      return response.data;
    } catch (error) {
      console.warn('Using mock data for reviews');
      // Fallback to mock data
      return [
        {
          id: '1',
          customerName: 'Monica G.',
          initials: 'MO',
          rating: 5,
          text: 'Per una laurea, ho pensato di sorprendere il festeggiato con un liquore personalizzato. La scelta di una delle ricette consigliate è stata azzeccatissima: un sapore eccellente che ha stupito tutti. La vera magia, però, è stata l\'etichetta personalizzata, che ha regalato un "effetto wow" indimenticabile. Consegna rapida e servizio top. Da provare assolutamente!',
          createdAt: '2024-03-20'
        },
        {
          id: '2',
          customerName: 'Mario F.',
          initials: 'MA',
          rating: 5,
          text: 'Prodotti straordinari e un\'idea davvero innovativa per chi cerca qualcosa di unico. Ho fatto diversi ordini e, una sola volta, ho riscontrato un problema con il pacco leggermente danneggiato. Il team di assistenza è stato impeccabile: hanno gestito la situazione con efficienza e rapidità. Davvero un servizio di alto livello!',
          createdAt: '2025-01-07'
        }
      ];
    }
  }
};

export const cartAPI = {
  // Get cart
  getCart: async (sessionId) => {
    try {
      const response = await apiClient.get(`/cart/${sessionId}`);
      return response.data;
    } catch (error) {
      console.warn('Using empty cart');
      return {
        sessionId,
        items: [],
        totalItems: 0,
        totalPrice: 0.0
      };
    }
  },

  // Add to cart
  addToCart: async (sessionId, item) => {
    try {
      const response = await apiClient.post('/cart/add', { sessionId, item });
      return response.data;
    } catch (error) {
      console.warn('Add to cart failed, using mock response');
      return { success: true, message: 'Item added to cart (mock)' };
    }
  },

  // Update cart item
  updateCartItem: async (sessionId, itemId, quantity) => {
    try {
      const response = await apiClient.put(`/cart/${sessionId}/${itemId}?quantity=${quantity}`);
      return response.data;
    } catch (error) {
      console.warn('Cart update failed');
      return { success: false, message: 'Failed to update cart' };
    }
  },

  // Remove cart item
  removeCartItem: async (sessionId, itemId) => {
    try {
      const response = await apiClient.delete(`/cart/${sessionId}/${itemId}`);
      return response.data;
    } catch (error) {
      console.warn('Cart item removal failed');
      return { success: false, message: 'Failed to remove item' };
    }
  },

  // Clear entire cart
  clearCart: async (sessionId) => {
    try {
      const response = await apiClient.delete(`/cart/${sessionId}`);
      return response.data;
    } catch (error) {
      console.warn('Cart clear failed');
      return { success: false, message: 'Failed to clear cart' };
    }
  }
};

export const giftCardsAPI = {
  // Create gift card
  createGiftCard: async (giftCardData) => {
    try {
      const response = await apiClient.post('/gift-cards', giftCardData);
      return response.data;
    } catch (error) {
      console.warn('Gift card creation failed, using mock response');
      return {
        success: true,
        code: `GC${Date.now()}`,
        message: 'Buono regalo creato con successo (mock)'
      };
    }
  },

  // Get gift card
  getGiftCard: async (code) => {
    try {
      const response = await apiClient.get(`/gift-cards/${code}`);
      return response.data;
    } catch (error) {
      console.warn(`Gift card ${code} not found`);
      return null;
    }
  }
};

export const newsletterAPI = {
  // Subscribe to newsletter
  subscribe: async (email) => {
    try {
      const response = await apiClient.post('/newsletter/subscribe', { email });
      return response.data;
    } catch (error) {
      console.warn('Newsletter subscription failed, using mock response');
      return { success: true, message: 'Iscrizione completata (mock)' };
    }
  }
};

export const contactAPI = {
  // Send contact message
  sendMessage: async (messageData) => {
    try {
      const response = await apiClient.post('/contact', messageData);
      return response.data;
    } catch (error) {
      console.warn('Contact message failed, using mock response');
      return { success: true, message: 'Messaggio inviato con successo (mock)' };
    }
  }
};

export const authAPI = {
  // Register private user
  registerPrivate: async (userData) => {
    try {
      const response = await apiClient.post('/auth/register/private', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Register business user  
  registerBusiness: async (userData) => {
    try {
      const response = await apiClient.post('/auth/register/business', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Login
  login: async (loginData) => {
    try {
      const response = await apiClient.post('/auth/login', loginData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get current user info
  getCurrentUser: async (token) => {
    try {
      const response = await apiClient.get('/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Logout
  logout: async (token) => {
    try {
      const response = await apiClient.post('/auth/logout', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update profile
  updateProfile: async (token, profileData) => {
    try {
      const response = await apiClient.put('/auth/profile', profileData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update business info
  updateBusinessInfo: async (token, businessData) => {
    try {
      const response = await apiClient.put('/auth/business-info', businessData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Address management
  getAddresses: async (token) => {
    try {
      const response = await apiClient.get('/auth/addresses', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateAddresses: async (token, addressData) => {
    try {
      const response = await apiClient.put('/auth/addresses', addressData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Verify token
  verifyToken: async (token) => {
    try {
      const response = await apiClient.post('/auth/verify-token', { token });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Verify email
  verifyEmail: async (token) => {
    try {
      const response = await apiClient.post('/auth/verify-email', { token });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Request password reset
  requestPasswordReset: async (email) => {
    try {
      const response = await apiClient.post('/auth/request-password-reset', { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reset password
  resetPassword: async (token, newPassword) => {
    try {
      const response = await apiClient.post('/auth/reset-password', { token, newPassword });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export const ordersAPI = {
  // Create order from cart
  createOrder: async (sessionId, orderData) => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Debug logging
      console.log('🔍 CreateOrder Debug:', {
        hasToken: !!token,
        tokenLength: token?.length,
        sessionId: sessionId,
        orderDataKeys: Object.keys(orderData)
      });
      
      if (!token) {
        console.error('❌ No auth token found in localStorage');
        throw new Error('Authentication required');
      }
      
      const response = await apiClient.post(`/orders/create?session_id=${sessionId}`, orderData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('❌ CreateOrder Error:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  },

  // Get user orders
  getMyOrders: async (page = 1, limit = 10) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await apiClient.get(`/orders/my-orders?page=${page}&limit=${limit}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get order by number or ID (public - for order confirmation)
  getOrderByNumber: async (orderIdentifier) => {
    try {
      const response = await apiClient.get(`/orders/confirmation/${orderIdentifier}`);
      return response.data;
    } catch (error) {
      console.error('Orders API Error:', error);
      return { success: false, message: 'Errore nel recupero dell\'ordine' };
    }
  },

  // Get order details
  getOrderDetails: async (orderId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await apiClient.get(`/orders/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Confirm payment
  confirmPayment: async (orderId, paymentData) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await apiClient.post(`/orders/${orderId}/confirm-payment`, paymentData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export const bottlesAPI = {
  // Get all bottles
  getBottles: async () => {
    try {
      const response = await apiClient.get('/bottles/');
      return response.data;
    } catch (error) {
      console.warn('Using mock data for bottles');
      // Fallback to mock data
      return [
        {
          id: 'classic',
          name: 'Bottiglia Classica',
          description: 'Bottiglia standard da 500ml',
          price: 0,
          image: 'https://images.unsplash.com/photo-1551538827-9c037cb4f32a?w=400&h=600&fit=crop'
        },
        {
          id: 'premium',
          name: 'Bottiglia Premium', 
          description: 'Bottiglia di vetro premium da 500ml',
          price: 8.00,
          image: 'https://images.unsplash.com/photo-1569529465841-dfecdab7503b?w=400&h=600&fit=crop'
        },
        {
          id: 'luxury',
          name: 'Bottiglia Luxury',
          description: 'Bottiglia di cristallo da 500ml',
          price: 15.00,
          image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=600&fit=crop'
        }
      ];
    }
  },

  // Get specific bottle
  getBottle: async (bottleId) => {
    try {
      const response = await apiClient.get(`/bottles/${bottleId}`);
      return response.data;
    } catch (error) {
      console.warn(`Using mock data for bottle: ${bottleId}`);
      return null;
    }
  }
};

export const adminAPI = {
  // Get all orders for admin
  getOrders: async (filters = {}, page = 1, limit = 20) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
      
      if (filters.status) params.append('status', filters.status);
      if (filters.paymentMethod) params.append('payment_method', filters.paymentMethod);
      if (filters.orderNumber) params.append('order_number', filters.orderNumber);
      if (filters.search && filters.search.trim()) params.append('search', filters.search.trim());
      
      const response = await apiClient.get(`/orders/admin/list?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get order details for admin
  getOrderDetails: async (orderId) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.get(`/orders/admin/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update order status
  updateOrderStatus: async (orderId, updateData) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.put(`/orders/admin/${orderId}/status`, updateData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get all users for admin
  getUsers: async (page = 1, limit = 20, filters = {}) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      // Add filter parameters
      if (filters.search) params.append('search', filters.search);
      if (filters.userType) params.append('userType', filters.userType);
      if (filters.status) params.append('status', filters.status);
      
      const response = await apiClient.get(`/auth/admin/users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update user by admin
  updateUser: async (userId, userData) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.put(`/auth/admin/users/${userId}`, userData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create user by admin
  createUser: async (userData) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.post('/auth/admin/users', userData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Dashboard statistics
  getDashboardStats: async () => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.get('/admin/dashboard/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Products management
  getProducts: async (filters = {}, page = 1, limit = 20) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
      
      if (filters.category) params.append('category', filters.category);
      if (filters.available !== undefined && filters.available !== '') params.append('available', filters.available.toString());
      if (filters.search) params.append('search', filters.search);
      
      const response = await apiClient.get(`/products/admin/list?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createProduct: async (productData) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.post('/products/admin/create', productData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateProduct: async (productId, productData) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.put(`/products/admin/${productId}`, productData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteProduct: async (productId) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.delete(`/products/admin/${productId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Ingredients management
  getIngredients: async (filters = {}, page = 1, limit = 20) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
      
      if (filters.category) params.append('category', filters.category);
      if (filters.available !== undefined && filters.available !== '') params.append('available', filters.available.toString());
      if (filters.search) params.append('search', filters.search);
      
      const response = await apiClient.get(`/ingredients/admin/list?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createIngredient: async (ingredientData) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.post('/ingredients/admin/create', ingredientData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateIngredient: async (ingredientId, ingredientData) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.put(`/ingredients/admin/${ingredientId}`, ingredientData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteIngredient: async (ingredientId) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.delete(`/ingredients/admin/${ingredientId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Bottles management
  getBottles: async (filters = {}, page = 1, limit = 20) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
      
      if (filters.material) params.append('material', filters.material);
      if (filters.available !== undefined && filters.available !== '') params.append('available', filters.available.toString());
      if (filters.search) params.append('search', filters.search);
      
      const response = await apiClient.get(`/bottles/admin/list?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createBottle: async (bottleData) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.post('/bottles/admin/create', bottleData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateBottle: async (bottleId, bottleData) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.put(`/bottles/admin/${bottleId}`, bottleData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteBottle: async (bottleId) => {
    try {
      const token = localStorage.getItem('adminAuthToken');
      const response = await apiClient.delete(`/bottles/admin/${bottleId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Format API
export const formatAPI = {
  // Get all active formats
  getAllFormats: async () => {
    try {
      const response = await apiClient.get('/formats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get single format by ID
  getFormat: async (formatId) => {
    try {
      const response = await apiClient.get(`/formats/${formatId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Export the main API client for custom requests
export default apiClient;